import React, { useState } from "react";
import axios from "axios";
import ReactJson from "react-json-view";
import logo from "./assets/logo_larefactoria.png";
import * as XLSX from "xlsx";
import exampleTickets from "./assets/exampleTickets.json";

const EXAMPLES_PATH = "tickets/";

function App() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showExamples, setShowExamples] = useState(false);
  const [examplePreview, setExamplePreview] = useState(null);
  const [exampleType, setExampleType] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [modalZoom, setModalZoom] = useState(1);
  const [modalOffset, setModalOffset] = useState({ x: 0, y: 0 });
  const [modalDragging, setModalDragging] = useState(false);
  const [modalDragStart, setModalDragStart] = useState({ x: 0, y: 0 });
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [touchStartDistance, setTouchStartDistance] = useState(null);
  const [modalTouchStartDistance, setModalTouchStartDistance] = useState(null);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    setFile(f);
    setResult(null);
    setError(null);
    setStatusMessage("");
    if (f) {
      if (f.type.startsWith("image/")) {
        setPreview(URL.createObjectURL(f));
      } else if (f.type === "application/pdf") {
        setPreview(null); // No preview para PDF
      } else {
        setPreview(null);
      }
    }
  };

  // Seleccionar ticket de ejemplo
  const handleSelectExample = (filename) => {
    setExamplePreview(EXAMPLES_PATH + filename);
  };

  // Cargar ticket de ejemplo como si fuera subido
  const handleUseExample = async () => {
    if (!examplePreview) return;
    setShowExamples(false);
    setResult(null);
    setError(null);
    setStatusMessage("");
    // Fetch el archivo y conviértelo a File
    const response = await fetch(examplePreview);
    const blob = await response.blob();
    const name = examplePreview.split("/").pop();
    const file = new File([blob], name, { type: blob.type });
    setFile(file);
    if (blob.type.startsWith("image/")) {
      setPreview(URL.createObjectURL(blob));
    } else {
      setPreview(null);
    }
  };

  const handleAnalyze = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setStatusMessage("Enviando a OCR IA...");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const responsePromise = axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/analyze-ticket`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: () => setStatusMessage("Enviando a OCR IA..."),
        }
      );
      setTimeout(() => setStatusMessage("Procesando OCR IA..."), 1000);
      setTimeout(() => setStatusMessage("Extrayendo texto..."), 3000);
      setTimeout(() => setStatusMessage("Enviando a OpenAI..."), 5000);
      setTimeout(() => setStatusMessage("Guardando en base de datos..."), 7000);
      const response = await responsePromise;
      setStatusMessage("¡Análisis completado!");
      setResult(response.data.result);
    } catch (err) {
      setError(err.response?.data?.detail || "Error analizando el ticket");
      setStatusMessage("");
    } finally {
      setLoading(false);
    }
  };

  // Extraer productos para la tabla
  let productos = [];
  let ticketInfo = null;
  try {
    ticketInfo = result ? (typeof result === 'string' ? JSON.parse(result) : result) : null;
    productos = ticketInfo && ticketInfo.productos ? ticketInfo.productos : [];
  } catch (e) {
    productos = [];
  }

  // Exportar productos a Excel
  const exportToExcel = () => {
    if (!productos.length) return;
    // Hoja de productos
    const wsProductos = XLSX.utils.json_to_sheet(productos);
    // Formato: encabezados en negrita y ancho de columnas
    const productosHeaders = Object.keys(productos[0] || {});
    wsProductos['!cols'] = productosHeaders.map(() => ({ wch: 20 }));
    // Hoja de cabecera del ticket
    const ticketCabecera = ticketInfo ? [{
      Fecha: ticketInfo.fecha || '',
      Establecimiento: ticketInfo.establecimiento || '',
      'Importe total': ticketInfo.importe_total || ''
    }] : [];
    const wsTicket = XLSX.utils.json_to_sheet(ticketCabecera);
    wsTicket['!cols'] = [
      { wch: 18 },
      { wch: 30 },
      { wch: 15 }
    ];
    // Libro
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, wsProductos, "Productos");
    XLSX.utils.book_append_sheet(wb, wsTicket, "Ticket");
    // Encabezados en negrita (solo visual en Excel, no en Google Sheets)
    if (wsProductos['A1']) wsProductos['A1'].s = { font: { bold: true } };
    if (wsTicket['A1']) wsTicket['A1'].s = { font: { bold: true } };
    XLSX.writeFile(wb, "ticket_analizado.xlsx");
  };

  // Reset zoom y offset al cambiar de imagen
  React.useEffect(() => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  }, [examplePreview]);

  // Handlers de zoom y drag
  const handleWheel = (e) => {
    e.preventDefault();
    let newZoom = zoom + (e.deltaY < 0 ? 0.1 : -0.1);
    newZoom = Math.max(1, Math.min(newZoom, 5));
    setZoom(newZoom);
  };
  const handleMouseDown = (e) => {
    if (zoom === 1) return;
    e.preventDefault();
    setDragging(true);
    setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
  };
  const handleMouseMove = (e) => {
    if (!dragging) return;
    e.preventDefault();
    setOffset({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const handleMouseUp = (e) => {
    if (dragging) e.preventDefault();
    setDragging(false);
  };
  const handleMouseLeave = (e) => {
    if (dragging) e.preventDefault();
    setDragging(false);
  };
  const handleResetZoom = () => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  };
  const handleZoomIn = () => setZoom(z => Math.min(z + 0.2, 5));
  const handleZoomOut = () => setZoom(z => Math.max(z - 0.2, 1));

  React.useEffect(() => {
    if (!showModal) {
      setModalZoom(1);
      setModalOffset({ x: 0, y: 0 });
      setModalDragging(false);
    }
  }, [showModal, preview]);

  const handleModalWheel = (e) => {
    e.preventDefault();
    let newZoom = modalZoom + (e.deltaY < 0 ? 0.1 : -0.1);
    newZoom = Math.max(1, Math.min(newZoom, 5));
    setModalZoom(newZoom);
  };
  const handleModalMouseDown = (e) => {
    if (modalZoom === 1) return;
    e.preventDefault();
    setModalDragging(true);
    setModalDragStart({ x: e.clientX - modalOffset.x, y: e.clientY - modalOffset.y });
  };
  const handleModalMouseMove = (e) => {
    if (!modalDragging) return;
    e.preventDefault();
    setModalOffset({ x: e.clientX - modalDragStart.x, y: e.clientY - modalDragStart.y });
  };
  const handleModalMouseUp = (e) => {
    if (modalDragging) e.preventDefault();
    setModalDragging(false);
  };
  const handleModalMouseLeave = (e) => {
    if (modalDragging) e.preventDefault();
    setModalDragging(false);
  };
  const handleModalResetZoom = () => {
    setModalZoom(1);
    setModalOffset({ x: 0, y: 0 });
  };
  const handleModalZoomIn = () => setModalZoom(z => Math.min(z + 0.2, 5));
  const handleModalZoomOut = () => setModalZoom(z => Math.max(z - 0.2, 1));

  React.useEffect(() => {
    if (showModal) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    return () => document.body.classList.remove('overflow-hidden');
  }, [showModal]);

  React.useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScrollTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Función para calcular la distancia entre dos puntos táctiles
  const getTouchDistance = (touch1, touch2) => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Handlers para zoom táctil
  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      setTouchStartDistance(getTouchDistance(e.touches[0], e.touches[1]));
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && touchStartDistance) {
      e.preventDefault();
      const currentDistance = getTouchDistance(e.touches[0], e.touches[1]);
      const scale = currentDistance / touchStartDistance;
      let newZoom = zoom * scale;
      newZoom = Math.max(1, Math.min(newZoom, 5));
      setZoom(newZoom);
      setTouchStartDistance(currentDistance);
    }
  };

  const handleTouchEnd = () => {
    setTouchStartDistance(null);
  };

  // Handlers para zoom táctil en modal
  const handleModalTouchStart = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      setModalTouchStartDistance(getTouchDistance(e.touches[0], e.touches[1]));
    }
  };

  const handleModalTouchMove = (e) => {
    if (e.touches.length === 2 && modalTouchStartDistance) {
      e.preventDefault();
      const currentDistance = getTouchDistance(e.touches[0], e.touches[1]);
      const scale = currentDistance / modalTouchStartDistance;
      let newZoom = modalZoom * scale;
      newZoom = Math.max(1, Math.min(newZoom, 5));
      setModalZoom(newZoom);
      setModalTouchStartDistance(currentDistance);
    }
  };

  const handleModalTouchEnd = () => {
    setModalTouchStartDistance(null);
  };

  return (
    <div className="bg-gray-100 min-h-screen">
      {/* Cabecera */}
      <header className="w-full py-6 glass no-rounded shadow-lg text-center mb-10 flex flex-col md:flex-row items-center md:justify-between px-4 md:px-8 gap-2 md:gap-0 relative border-b border-gray-200 animate-fadein">
        <a href="https://larefactoria.com" target="_blank" rel="noopener noreferrer"
          className="absolute left-4 top-4 md:static md:left-0 md:top-0">
          <img src={logo} alt="Logo La Refactoria" className="h-8 w-auto md:h-12 mb-2 md:mb-0 drop-shadow-lg" />
        </a>
        <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4 md:flex-1 justify-center">
          <h1 className="titulo-app text-3xl md:text-5xl font-extrabold text-gray-900 text-center md:flex-1 mt-12 md:mt-0 tracking-tight drop-shadow-lg">Ticket Analyzer Concept</h1>
        </div>
        <button
          className="right-4 top-4 bg-white/80 rounded-full px-4 py-2 text-sm font-semibold text-gray-900 shadow border border-gray-300 focus:outline-none z-10 hover:bg-gray-100 transition absolute md:static md:self-center"
          title="¿Qué hace esta aplicación?"
          onClick={() => setShowInfo(true)}
        >
          ¿Qué hace esta aplicación?
        </button>
        <div className="w-12 hidden md:block" /> {/* Espacio para equilibrar el layout en desktop */}
      </header>
      <div className="flex flex-col md:flex-row items-start justify-center p-2 md:p-4 gap-4 md:gap-8 max-w-5xl mx-auto h-auto md:h-[calc(100vh-176px)]">
        {/* Panel principal */}
        <div className="glass shadow-2xl p-8 w-full max-w-md mx-auto animate-fadein-slow min-h-[500px] h-full flex flex-col sticky top-8">
          <div className="flex flex-col md:flex-row gap-2 mb-4">
            <label className="flex-1 h-12">
              <input
                type="file"
                accept="image/*,application/pdf"
                onChange={handleFileChange}
                className="hidden"
              />
              <div className="w-full h-12 flex items-center justify-center bg-white border border-gray-400 rounded-lg px-3 py-2 text-gray-800 font-semibold text-center hover:bg-gray-100 transition shadow-sm cursor-pointer">
                {file ? file.name : "Seleccionar archivo"}
              </div>
            </label>
            <button
              type="button"
              className="flex-1 h-12 bg-gradient-to-r from-gray-700 via-gray-800 to-black text-white px-3 py-2 rounded-lg font-semibold hover:from-gray-800 hover:to-gray-900 border-none transition shadow-lg flex items-center justify-center animate-pop"
              onClick={() => { setShowExamples(true); setExamplePreview(null); }}
            >
              Ticket de ejemplo
            </button>
          </div>
          {preview && (
            <div className="mb-4 flex flex-col items-center flex-1">
              <div
                className="relative flex items-center justify-center overflow-hidden border rounded bg-gray-50 w-full h-full max-h-full"
                style={{ maxWidth: 320, minHeight: 0, minWidth: 0, flex: 1, cursor: zoom > 1 ? (dragging ? 'grabbing' : 'grab') : 'pointer' }}
                onWheel={handleWheel}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <img
                  src={preview}
                  alt="Vista previa"
                  style={{
                    transform: `scale(${zoom}) translate(${offset.x / zoom}px, ${offset.y / zoom}px)`,
                    transition: dragging ? 'none' : 'transform 0.2s',
                    userSelect: 'none',
                    maxHeight: '100%',
                    maxWidth: '100%',
                    height: '100%',
                    width: 'auto',
                    objectFit: 'contain'
                  }}
                  draggable={false}
                  onDragStart={e => e.preventDefault()}
                />
              </div>
              <div className="flex gap-2 mt-2">
                <button onClick={handleZoomOut} className="px-2 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300 font-bold">-</button>
                <button onClick={handleZoomIn} className="px-2 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300 font-bold">+</button>
                <button onClick={handleResetZoom} className="px-2 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300">Restablecer</button>
              </div>
            </div>
          )}
          {file && file.type === "application/pdf" && (
            <div className="mb-4 text-center text-gray-500">PDF seleccionado: {file.name}</div>
          )}
          <button
            onClick={handleAnalyze}
            disabled={!file || loading}
            className="w-full bg-gradient-to-r from-gray-700 via-gray-800 to-black text-white py-2 rounded-lg font-semibold hover:from-gray-800 hover:to-gray-900 transition disabled:opacity-50 mb-2 shadow-lg animate-pop"
          >
            {loading ? "Analizando..." : "Analizar Ticket "}
          </button>
          {statusMessage && (
            <div className="mt-4 text-blue-600 text-center animate-pulse">{statusMessage}</div>
          )}
          {error && (
            <div className="mt-4 text-red-600 text-center">{error}</div>
          )}
          {/* Solo muestro el JSON si no hay productos */}
          {result && productos.length === 0 && (
            <div className="mt-6">
              <h2 className="font-semibold mb-2">Resultado JSON:</h2>
              <ReactJson src={typeof result === 'string' ? JSON.parse(result) : result} collapsed={1} enableClipboard={false} displayDataTypes={false} />
            </div>
          )}
        </div>
        {/* Panel lateral derecho para la tabla */}
        {productos.length > 0 && (
          <div className="glass shadow-2xl p-6 w-full max-w-2xl mx-auto border border-gray-200 backdrop-blur-md animate-fadein-slow">
            {/* Info cabecera del ticket */}
            {ticketInfo && (
              <div className="mb-4 flex flex-col md:flex-row md:flex-wrap md:items-center md:justify-between gap-2 md:gap-6 p-4 bg-gray-100 rounded-lg border border-gray-200">
                <div className="whitespace-normal break-all min-w-0"><span className="font-semibold text-gray-900">Fecha:</span> {ticketInfo.fecha || '-'}</div>
                <div className="flex items-center min-w-0"><span className="font-semibold text-gray-900 flex-shrink-0">Establecimiento:</span><span className="ml-1 truncate min-w-0">{ticketInfo.establecimiento || '-'}</span></div>
                <div className="whitespace-normal break-all min-w-0"><span className="font-semibold text-gray-900">Importe total:</span> {ticketInfo.importe_total || '-'}</div>
                <div className="whitespace-normal break-all min-w-0"><span className="font-semibold text-gray-800">PHILADELPHIA detectados:</span> {productos.filter(p => p.producto_asociado && p.producto_asociado.trim() !== '').length}</div>
              </div>
            )}
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">Productos del Ticket</h2>
              <button
                onClick={exportToExcel}
                className="bg-gradient-to-r from-gray-700 via-gray-800 to-black text-white px-4 py-2 rounded-lg font-semibold hover:from-gray-800 hover:to-gray-900 transition shadow animate-pop"
              >
                Exportar a Excel
              </button>
            </div>
            {/* Tabla en escritorio */}
            <div className="w-full overflow-x-auto hidden">
              <table className="min-w-full border border-gray-300 rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-gradient-to-r from-gray-200 via-gray-300 to-gray-400">
                    <th className="px-3 py-2 border-b text-left text-gray-800 font-semibold">Nombre</th>
                    <th className="px-3 py-2 border-b text-left text-gray-800 font-semibold">Código</th>
                    <th className="px-3 py-2 border-b text-right text-gray-800 font-semibold">Precio</th>
                    <th className="px-3 py-2 border-b text-right text-gray-800 font-semibold">Unidades</th>
                    <th className="px-3 py-2 border-b text-left text-gray-800 font-semibold">Asociado</th>
                    <th className="px-3 py-2 border-b text-right text-gray-800 font-semibold">% Match</th>
                  </tr>
                </thead>
                <tbody>
                  {productos.map((prod, idx) => (
                    <tr
                      key={idx}
                      className={
                        (prod.producto_asociado && prod.producto_asociado.trim() !== "")
                          ? "bg-green-50 text-green-900 hover:bg-green-100 animate-fadein"
                          : idx % 2 === 0
                            ? "bg-white hover:bg-gray-50"
                            : "bg-gray-50 hover:bg-gray-100"
                      }
                    >
                      <td className="px-3 py-2 border-b flex items-center gap-2">{prod.nombre_producto}
                        {prod.producto_asociado && prod.producto_asociado.trim() !== "" && (
                          <span className="ml-2 px-2 py-0.5 rounded-full bg-green-600 text-white text-xs font-bold shadow flex items-center justify-center" title="Producto asociado">
                            <svg xmlns='http://www.w3.org/2000/svg' className='h-3 w-3' fill='none' viewBox='0 0 16 16' stroke='currentColor' strokeWidth='2'>
                              <path strokeLinecap='round' strokeLinejoin='round' d='M4 8l3 3 5-5' />
                            </svg>
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 border-b">{prod.codigo}</td>
                      <td className="px-3 py-2 border-b text-right">{prod.precio}</td>
                      <td className="px-3 py-2 border-b text-right">{prod.unidades}</td>
                      <td className="px-3 py-2 border-b">{prod.producto_asociado || '-'}</td>
                      <td className="px-3 py-2 border-b text-right">{prod.porcentaje_matching != null ? prod.porcentaje_matching + '%' : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Tarjetas en móvil */}
            <div className="block space-y-4">
              {productos.map((prod, idx) => (
                <div
                  key={idx}
                  className={
                    "rounded-lg p-4 shadow border " +
                    ((prod.producto_asociado && prod.producto_asociado.trim() !== "")
                      ? "bg-green-50 border-green-200 animate-fadein"
                      : "bg-gray-50 border-gray-200")
                  }
                >
                  <div className="font-bold text-lg text-gray-900 mb-1">{prod.nombre_producto}</div>
                  <div className="flex items-center justify-between text-sm text-gray-700">
                    <span><span className="font-medium">Código:</span> {prod.codigo}</span>
                    <span><span className="font-medium">Unidades:</span> {prod.unidades}</span>
                    <span className="font-semibold text-green-700"><span className="font-medium">Precio:</span> {prod.precio}€</span>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    {prod.producto_asociado && prod.producto_asociado.trim() !== "" ? (
                      <>
                        <span className="px-2 py-0.5 rounded-full bg-green-600 text-white text-xs font-bold shadow flex items-center justify-center" title="Producto asociado">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 8l3 3 5-5" />
                          </svg>
                        </span>
                        <span className="text-green-800 font-semibold">{prod.producto_asociado}</span>
                      </>
                    ) : (
                      <span className="text-gray-400 italic">No asociado</span>
                    )}
                  </div>
                  <div className="flex justify-end mt-2">
                    <span className={
                      "px-3 py-1 rounded-full text-xs font-bold shadow " +
                      (prod.porcentaje_matching >= 70
                        ? "bg-green-600 text-white"
                        : prod.porcentaje_matching >= 30
                          ? "bg-yellow-400 text-gray-900"
                          : "bg-gray-300 text-gray-700")
                    }>
                      % Match: {prod.porcentaje_matching != null ? prod.porcentaje_matching + '%' : '-'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {/* Modal para previsualizar la imagen en grande */}
      {showModal && preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-xl shadow-lg p-4 max-w-3xl w-full flex flex-col items-center" onClick={e => e.stopPropagation()}>
            <div className="w-full flex justify-end gap-2 mb-2">
              <button onClick={handleModalZoomOut} className="px-2 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300 font-bold">-</button>
              <button onClick={handleModalZoomIn} className="px-2 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300 font-bold">+</button>
              <button onClick={handleModalResetZoom} className="px-2 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300">Restablecer</button>
            </div>
            <div
              className="w-full flex items-center justify-center overflow-hidden border rounded bg-gray-50 relative"
              style={{ height: 400, cursor: modalZoom > 1 ? (modalDragging ? 'grabbing' : 'grab') : 'default' }}
              onWheel={handleModalWheel}
              onMouseDown={handleModalMouseDown}
              onMouseMove={handleModalMouseMove}
              onMouseUp={handleModalMouseUp}
              onMouseLeave={handleModalMouseLeave}
              onTouchStart={handleModalTouchStart}
              onTouchMove={handleModalTouchMove}
              onTouchEnd={handleModalTouchEnd}
            >
              <img
                src={preview}
                alt="Ticket grande"
                style={{
                  transform: `scale(${modalZoom}) translate(${modalOffset.x / modalZoom}px, ${modalOffset.y / modalZoom}px)`,
                  transition: modalDragging ? 'none' : 'transform 0.2s',
                  userSelect: 'none',
                  maxHeight: '400px',
                  maxWidth: '100%'
                }}
                draggable={false}
                onDragStart={e => e.preventDefault()}
              />
            </div>
            <button className="mt-4 px-6 py-2 bg-gradient-to-r from-gray-700 via-gray-800 to-black text-white rounded-lg hover:from-gray-800 hover:to-gray-900" onClick={() => setShowModal(false)}>
              Cerrar
            </button>
          </div>
        </div>
      )}
      {/* Popup de ejemplos */}
      {showExamples && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60" onClick={() => setShowExamples(false)}>
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-lg w-full relative" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold mb-4 text-center">Selecciona un ticket de ejemplo</h2>
            <div className="grid grid-cols-2 gap-4 max-h-96 overflow-y-auto mb-4">
              {exampleTickets.map((filename, idx) => (
                <div key={idx} className={`border rounded p-2 cursor-pointer hover:border-blue-500 ${examplePreview === EXAMPLES_PATH + filename ? 'border-blue-600' : 'border-gray-200'}`}
                  onClick={() => handleSelectExample(filename)}>
                  {filename.match(/\.(jpg|jpeg|png)$/i) ? (
                    <img src={EXAMPLES_PATH + filename} alt={filename} className="w-full h-32 object-contain mb-2" />
                  ) : (
                    <div className="w-full h-32 flex items-center justify-center bg-gray-100 text-gray-500 mb-2">
                      <span className="text-4xl">📄</span>
                    </div>
                  )}
                  <div className="text-xs text-center break-all">{filename}</div>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <button className="px-4 py-2 rounded bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold" onClick={() => setShowExamples(false)}>Cancelar</button>
              <button className="px-4 py-2 rounded bg-gradient-to-r from-gray-700 via-gray-800 to-black hover:from-gray-800 hover:to-gray-900 text-white font-semibold disabled:opacity-50" disabled={!examplePreview} onClick={handleUseExample}>Usar este ticket</button>
            </div>
          </div>
        </div>
      )}
      {/* Popup de información */}
      {showInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60" onClick={() => setShowInfo(false)}>
          <div className="bg-white rounded-xl shadow-lg p-6 max-w-lg w-full relative" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold mb-4 text-center">¿Qué hace esta aplicación?</h2>
            <p className="mb-4 text-gray-700">
              Esta aplicación detecta productos de un ticket de compra de supermercado y asocia los productos de PHILADELPHIA existentes al producto correspondiente de la siguiente lista:
            </p>
            <ul className="mb-4 text-sm text-gray-800 list-disc pl-6">
              <li>PHIL 100G REGULAR 28CA</li>
              <li>PHIL 200G LIGHT 16CA</li>
              <li>PHIL 200G REGULAR 16CA</li>
              <li>PHIL 250G LIGHT 16CA</li>
              <li>PHIL 250G REG 16CA</li>
              <li>PHIL 2X250G FULL FAT 8CA</li>
              <li>PHIL 2X250G LIGHT 8CA</li>
              <li>PHILA 270G FULL FAT 16CA</li>
              <li>PHILA 270G LIGHT 16CA</li>
              <li>PHILADELPHIA 350G REGULAR 16CA</li>
              <li>PHILA 350G LIGHT 16CA</li>
              <li>PHILADELPHIA LACTOSE FREE 150G 10CA</li>
              <li>PHIL 150G MIXED HERBS 10CA</li>
              <li>PHIL 150G SALMON 10CA</li>
              <li>PHIL 150GR TRUFA 10CA</li>
              <li>PHIL 145 GR PLANT BASED 10CA</li>
            </ul>
            <div className="flex justify-end">
              <button className="px-4 py-2 rounded bg-gradient-to-r from-gray-700 via-gray-800 to-black hover:from-gray-800 hover:to-gray-900 text-white font-semibold" onClick={() => setShowInfo(false)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
      <footer className="w-full text-center text-gray-400 text-xs py-4 mt-8">
        <a href="https://larefactoria.com" target="_blank" rel="noopener noreferrer" className="hover:underline">
          <span className="font-semibold text-gray-900">La Refactoría</span> &copy; {new Date().getFullYear()}
        </a>
      </footer>
      {/* Botón flotante para subir arriba */}
      {showScrollTop && (
        <button
          onClick={handleScrollTop}
          className="fixed bottom-6 right-6 z-50 bg-gray-800 text-white rounded-full shadow-lg p-3 hover:bg-gray-900 transition flex items-center justify-center animate-pop"
          title="Subir arriba"
          style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.18)' }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </button>
      )}
    </div>
  );
}

export default App;